import { ChevronRight } from "lucide-react";
import { ActiveLink, useFullPath } from "raviger";
import { Fragment, ReactNode, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

import { Avatar } from "@/components/Common/Avatar";

const isChildActive = (link: NavigationLink) => {
  if (!link.children) return false;
  const currentPath = window.location.pathname;
  return link.children.some((child) => currentPath.startsWith(child.url));
};

export interface NavigationLink {
  header?: string;
  headerIcon?: ReactNode;
  name: string;
  url: string;
  icon?: ReactNode;
  visibility?: boolean;
  children?: NavigationLink[];
}

export function NavMain({ links }: { links: NavigationLink[] }) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const fullPath = useFullPath();
  const fullPathMap = useMemo(
    () =>
      fullPath.split("/").reduce(
        (acc, part) => ({
          ...acc,
          [part]: true,
        }),
        {} as Record<string, boolean>,
      ),
    [fullPath],
  );

  return (
    <SidebarGroup>
      <SidebarMenu>
        {links
          .filter((link) => link.visibility !== false)
          .map((link) => (
            <Fragment key={link.name}>
              {link.children ? (
                isCollapsed ? (
                  <PopoverMenu link={link} />
                ) : (
                  <Collapsible
                    asChild
                    defaultOpen={isChildActive(link)}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          tooltip={link.name}
                          className="cursor-pointer text-sidebar-foreground hover:bg-gray-200 hover:text-primary-700 dark:hover:bg-gray-800 dark:hover:text-gray-50"
                        >
                          {link.icon ? (
                            link.icon
                          ) : (
                            <Avatar
                              name={link.name}
                              className="size-6 -m-1 rounded-sm"
                            />
                          )}
                          <span className="group-data-[collapsible=icon]:hidden ml-1">
                            {link.name}
                          </span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub className="border-l border-gray-300">
                          {link.children
                            ?.filter((link) => link.visibility !== false)
                            .map((subItem) => (
                              <Fragment key={subItem.name}>
                                {subItem.header && (
                                  <div className="flex items-center gap-2 mt-2">
                                    {subItem.headerIcon}
                                    <span className="text-gray-400 uppercase text-xs font-bold">
                                      {subItem.header}
                                    </span>
                                  </div>
                                )}
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton
                                    asChild
                                    className={
                                      "text-sidebar-foreground transition font-normal hover:bg-gray-200 hover:text-primary-700 dark:hover:bg-gray-800 dark:hover:text-gray-50"
                                    }
                                  >
                                    <ActiveLink
                                      href={subItem.url}
                                      className="flex min-w-0 w-full"
                                      activeClass={cn(
                                        subItem.url
                                          .split("/")
                                          .every((part) => fullPathMap[part]) &&
                                          "bg-white text-primary-700 shadow dark:bg-gray-200 dark:text-gray-900",
                                      )}
                                      exactActiveClass="bg-white text-primary-700 shadow dark:bg-gray-200 dark:text-gray-900"
                                    >
                                      <span className="truncate">
                                        {subItem.name}
                                      </span>
                                    </ActiveLink>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              </Fragment>
                            ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={link.name}
                    className={
                      "text-sidebar-foreground transition font-normal hover:bg-gray-200 hover:text-primary-700 dark:hover:bg-gray-800 dark:hover:text-gray-50"
                    }
                  >
                    <ActiveLink
                      href={link.url}
                      activeClass="bg-white text-primary-700 shadow-sm dark:bg-gray-200 dark:text-gray-900"
                      exactActiveClass="bg-white text-primary-700 shadow-sm dark:bg-gray-200 dark:text-gray-900"
                    >
                      {link.icon ? (
                        link.icon
                      ) : (
                        <Avatar
                          name={link.name}
                          className="size-6 -m-1 rounded-sm"
                        />
                      )}

                      <span className="group-data-[collapsible=icon]:hidden ml-1">
                        {link.name}
                      </span>
                    </ActiveLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </Fragment>
          ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function PopoverMenu({ link }: { link: NavigationLink }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          aria-label={link.name}
          tooltip={link.name}
          className={cn(
            "cursor-pointer text-sidebar-foreground hover:bg-gray-200 hover:text-primary-700 dark:hover:bg-gray-800 dark:hover:text-gray-50",
            {
              "bg-white text-primary-700 shadow dark:bg-gray-200 dark:text-gray-900":
                isChildActive(link),
            },
          )}
        >
          {link.icon ? (
            link.icon
          ) : (
            <Avatar name={link.name} className="size-6 -m-1 rounded-sm" />
          )}
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-48 p-1"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1">
          {link.children
            ?.filter((subItem) => subItem.visibility !== false)
            .map((subItem) => (
              <ActiveLink
                key={subItem.name}
                href={subItem.url}
                onClick={() => setOpen(false)}
                className="w-full rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100"
                activeClass="bg-gray-100 text-primary-700 dark:bg-gray-800 dark:text-gray-50"
                exactActiveClass="bg-gray-100 text-primary-700 dark:bg-gray-800 dark:text-gray-50"
              >
                {subItem.name}
              </ActiveLink>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
